import { createTheme, alpha } from "@mui/material/styles";
import { DEFAULT_THEME_COLORS, THEME_PRESET_COLORS, UI_COLORS } from "./colorConstants";
import { getEffectiveThemeMode } from "../state/slices/theme";

// Default color palette
const defaultColors = DEFAULT_THEME_COLORS;

/**
 * Creates a Material-UI theme based on the theme state
 * @param {Object} themeState - Theme state from Redux (mode, primaryColor, secondaryColor, customColors)
 * @returns {Object} Material-UI theme object
 */
export const createAppTheme = (themeState, systemPreference = null) => {
  const { mode = "light", primaryColor, secondaryColor, customColors = {} } = themeState;
  

  let effectiveMode;
  if (mode === "system") {
    effectiveMode = systemPreference || getEffectiveThemeMode(mode);
  } else {
    effectiveMode = mode;
  }
  
  const colors = defaultColors[effectiveMode] || defaultColors.light;
  
  // Use light theme primary color as base for consistency across both themes
  // This ensures --theme-primary is the same in both light and dark themes
  const primary = primaryColor || defaultColors.light.primary;
  const secondary = secondaryColor || colors.secondary;
  
  // Calculate primary dark and light colors from base primary color (light mode default)
  // This ensures primary-dark and primary-light are consistent across both themes
  const basePrimary = primaryColor || defaultColors.light.primary;
  const primaryDark = customColors.primaryDark || alpha(basePrimary, 0.9);
  const primaryLight = customColors.primaryLight || alpha(basePrimary, 0.5);
  
  const theme = createTheme({
    palette: {
      mode: effectiveMode,
      primary: {
        main: primary,
        light: primaryLight,
        dark: primaryDark,
        contrastText: customColors.primaryContrast || undefined,
      },
      secondary: {
        main: secondary,
        light: customColors.secondaryLight || undefined,
        dark: customColors.secondaryDark || undefined,
        contrastText: customColors.secondaryContrast || undefined,
      },
      background: {
        default: customColors.backgroundDefault || colors.background,
        paper: customColors.backgroundPaper || colors.paper || colors.background, 
      },
      text: {
        primary: customColors.textPrimary || colors.text.primary,
        secondary: customColors.textSecondary || colors.text.secondary,
      },
      ...(customColors.error && { error: { main: customColors.error } }),
      ...(customColors.warning && { warning: { main: customColors.warning } }),
      ...(customColors.info && { info: { main: customColors.info } }),
      ...(customColors.success && { success: { main: customColors.success } }),
    },
    typography: {
      fontFamily: [
        "Poppins",
        "Inter",
        "DM Sans",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
          },
          containedPrimary: {
            backgroundColor: primary,
            "&:hover": {
              backgroundColor: primaryDark,
            },
            "&:focus": {
              backgroundColor: primaryDark,
              boxShadow: `0 0 0 0.2rem ${alpha(primary, 0.25)}`,
            },
            "&:focus-visible": {
              outline: `2px solid ${primary}`,
              outlineOffset: "2px",
            },
          },
          outlinedPrimary: {
            borderColor: primary,
            color: primary,
            "&:hover": {
              backgroundColor: primary,
              borderColor: primary,
              color: UI_COLORS.white,
            },
            "&:focus": {
              backgroundColor: alpha(primary, 0.08),
              borderColor: primary,
              boxShadow: `0 0 0 0.2rem ${alpha(primary, 0.25)}`,
            },
            "&:focus-visible": {
              outline: `2px solid ${primary}`,
              outlineOffset: "2px",
            },
          },
          textPrimary: {
            color: primary,
            "&:hover": {
              backgroundColor: effectiveMode === "dark" 
                ? UI_COLORS.overlay.dark
                : UI_COLORS.overlay.light,
            },
            "&:focus": {
              backgroundColor: effectiveMode === "dark" 
                ? UI_COLORS.overlay.dark
                : UI_COLORS.overlay.light,
              boxShadow: `0 0 0 0.2rem ${alpha(primary, 0.25)}`,
            },
            "&:focus-visible": {
              outline: `2px solid ${primary}`,
              outlineOffset: "2px",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: effectiveMode === "dark" 
              ? `0px 2px 8px ${alpha(UI_COLORS.black, 0.3)}`
              : `0px 2px 8px ${alpha(UI_COLORS.black, 0.1)}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  });

  return theme;
};

/**
 * Predefined theme presets
 */
export const themePresets = THEME_PRESET_COLORS;


